/**
 * Customer API hooks —— React Query 封裝。
 * 從 @app/shared 取 type，避免前後端型別漂移（對齊 05 §6.1）。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCustomerInput,
  CustomerListQuery,
  CustomerNeighborQuery,
  CustomerResponse,
  CustomerSearchableField,
  UpdateCustomerInput,
} from '@app/shared';
import type { ApiList, ApiSuccess } from '@app/shared';
import { apiClient } from '../../lib/api-client';

export function useCustomers(query: Partial<CustomerListQuery> = {}) {
  return useQuery({
    queryKey: ['customers', query],
    queryFn: async (): Promise<ApiList<CustomerResponse>> => {
      const res = await apiClient.get<ApiList<CustomerResponse>>('/customers', { params: query });
      return res.data;
    },
  });
}

export function useCustomer(id: number | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    enabled: id != null,
    queryFn: async (): Promise<ApiSuccess<CustomerResponse>> => {
      const res = await apiClient.get<ApiSuccess<CustomerResponse>>(`/customers/${id}`);
      return res.data;
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomerInput): Promise<CustomerResponse> => {
      const res = await apiClient.post<ApiSuccess<CustomerResponse>>('/customers', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomer(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCustomerInput): Promise<CustomerResponse> => {
      const res = await apiClient.patch<ApiSuccess<CustomerResponse>>(`/customers/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer', id] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/customers/${id}`);
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer', id] });
    },
  });
}

/**
 * REQ-20260418015 操作台翻頁：取得上一筆 / 下一筆的客戶 id。
 * 回傳 null 表示已經是邊界（沒有更前 / 更後的客戶）。
 */
export function useCustomerNeighbor(
  id: number | undefined,
  query: CustomerNeighborQuery,
) {
  return useQuery({
    queryKey: ['customer-neighbor', id, query],
    enabled: id != null,
    queryFn: async (): Promise<{ id: number } | null> => {
      const res = await apiClient.get<{ data: { id: number } | null }>(
        `/customers/${id}/neighbor`,
        { params: query },
      );
      return res.data.data;
    },
  });
}

/**
 * REQ-20260418015 搜尋 Modal：單欄位 LIKE 搜尋。
 * 最多 20 筆；超過則需使用者縮小關鍵字。
 * q 為空時不觸發請求。
 */
export function useCustomerFieldSearch(params: {
  field: CustomerSearchableField;
  q: string;
  enabled?: boolean;
}) {
  const { field, q, enabled = true } = params;
  const trimmed = q.trim();
  return useQuery({
    queryKey: ['customer-field-search', field, trimmed],
    enabled: enabled && trimmed.length > 0,
    queryFn: async (): Promise<ApiList<CustomerResponse>> => {
      const res = await apiClient.get<ApiList<CustomerResponse>>('/customers', {
        params: {
          field,
          q: trimmed,
          pageSize: 20,
          sort: field,
          order: 'asc',
        },
      });
      return res.data;
    },
  });
}

/**
 * REQ-20260912001 客戶編號自動流水號:給前 3 碼分類碼,取下一個可用的 9 碼編號。
 * 刻意用一般 async 函式而非 useQuery —— 建完客戶後號碼就變了,快取回舊號會直接撞 409。
 * 該前綴號碼用完時回 null(前端就不填,也不報錯)。
 */
export async function fetchNextCustomerCode(prefix: string): Promise<string | null> {
  const res = await apiClient.get<ApiSuccess<{ code: string | null }>>('/customers/next-code', {
    params: { prefix },
  });
  return res.data.data.code;
}
